import { from } from "linq-to-typescript";
import { SpartaxAuftragskunde, SpartaxAuftragskundeArt, SpartaxAuftragskundeUtil, SpartaxErklaerungsjahrUtil, SpartaxSchaetzungsJahreseinkunftArtOrder } from "./spartax-auftragskunde.dto";
import { SpartaxDocument, SpartaxDocumentType } from "./spartax-document.dto";
import { SpartaxKind, SpartaxKunde, SpartaxKundeUtil } from "./spartax-kunde.dto";
import { SpartaxKonfigProvisionen, SpartaxPartner } from "./spartax-partner.dto";
import { SpartaxUser } from "./spartax-user.dto";
import { SpartaxFrage } from "./spartax-frage.dto";
import * as moment from "moment";

type SpartaxRechnung = {
    //Just a stub
    BetragNetto?: number;
    KundeFormModelId?: string;
    Stornodatum?: Date;
    Status?: string;
};

type SpartaxSevDeskReference = {
    //Just a stub
    Id?: string;
};

type SpartaxKommunikation = {
    //Just a stub
};

/**
 * Hilfsfunktion: Prüft ob eine Rechnung storniert ist
 * - SevDesk: Stornodatum ist gesetzt
 * - FreeFinance: Status === 'CANCELLED'
 */
export function isRechnungStorniert(rechnung: SpartaxRechnung): boolean {
    return !!rechnung.Stornodatum || rechnung.Status === 'CANCELLED';
}

/**
 * Hilfsfunktion: Prüft ob eine Rechnung NICHT storniert ist
 * - SevDesk: Stornodatum ist nicht gesetzt
 * - FreeFinance: Status !== 'CANCELLED'
 */
export function isRechnungAktiv(rechnung: SpartaxRechnung): boolean {
    return !isRechnungStorniert(rechnung);
}

//WARNING - Types MUST be kept in Sync between the Spartax Azure Function Project and with the ones in Beeexpert manually

// Vollmacht-Pflicht gilt erst für Aufträge ab diesem Datum (Auftrag.Created).
// Für ältere Aufträge (Bestandsschutz für offene Aufträge wo das Gespräch vor der Einführung der Pflicht stattfand)
// wird die Vollmacht nicht als fehlender Beleg geführt und blockiert daher auch nicht den Statuswechsel.
const VOLLMACHT_PFLICHT_AB = '2026-05-06';
const VOLLMACHT_DEBUG_AUFTRAG_LISTITEMID = [208]; // Bei diesen Aufträgen wird die Vollmacht-Pflicht unabhängig vom Created-Datum geprüft (für Testzwecke)

export enum Auftragstatus {
    Neu = 'Neu',
    waD = 'waD',
    iB = 'iB',
    waB = 'waB',
    iA = 'iA',
    Ro = 'Ro',
    Rb = 'Rb',
    Abg = 'Abg',
    Sto = 'Sto',
}

export const AuftragStatusName = new Map<string, string>([
    [Auftragstatus.Neu, 'Neuer Autrag wurde generiert'],
    [Auftragstatus.waD, 'Warten auf Dokumente'],
    [Auftragstatus.iB, 'In Bearbeitung'],
    [Auftragstatus.waB, 'Warten auf Bescheid'],
    [Auftragstatus.iA, 'In Abrechnung'],
    [Auftragstatus.Ro, 'Rechnungen offen'],
    [Auftragstatus.Rb, 'Rechnungen bezahlt'],
    [Auftragstatus.Abg, 'Abgeschlossen'],
    [Auftragstatus.Sto, 'Storniert'],
]);

export const AuftragStatusReihenholge = new Map<string, number>([
    [Auftragstatus.Neu, 0],
    [Auftragstatus.waD, 1],
    [Auftragstatus.iB, 2],
    [Auftragstatus.waB, 3],
    [Auftragstatus.iA, 4],
    [Auftragstatus.Ro, 5],
    [Auftragstatus.Rb, 6],
    [Auftragstatus.Abg, 7],
]);

/** Lineare Statuskette. Storniert (Sto) ist bewusst nicht enthalten. */
export const AuftragStatusOrder: Auftragstatus[] = [
    Auftragstatus.Neu,
    Auftragstatus.waD,
    Auftragstatus.iB,
    Auftragstatus.waB,
    Auftragstatus.iA,
    Auftragstatus.Ro,
    Auftragstatus.Rb,
    Auftragstatus.Abg,
];

export const AuftragsStatusAutoNext = [
    Auftragstatus.Neu,
    Auftragstatus.waD,
    Auftragstatus.iB,
    Auftragstatus.waB,
];

export enum AuftragFehlerstatus {
    E000 = 'E000',
    E100 = 'E100',
}

export const AuftragFehlerStatusName = new Map<string, string>([
    [AuftragFehlerstatus.E000, 'E000 - Alles Ok'],
    [AuftragFehlerstatus.E100, 'E100 - Partnerermittlung fehlgeschlagen']
]);

export type SpartaxKommunikationEintragAlt = {
    Created?: Date;
    Author?: string;
    Note?: string;
};

export type SpartaxKonfigHonorare = {
    GueltigAb?: Date;
    BasisMin?: number;
    BasisMax?: number;
    SatzMin?: number;
    SatzMax?: number;
    Wiederaufnahme?: number;
    Pflichtveranlagung?: number;
    EAGewerbe?: number;
    EAVermietung?: number;
};

export type SpartaxKonfigJahreseinkunft = {
    ListItemId: number;
    Jahr: number;
    Wert1: number;
    Wert2: number;
    Wert3: number;
};

export type SpartaxStatusHistorieEintrag = {
    Status: Auftragstatus;
    Seit: Date;
};

export type SpartaxAuftrag = {
    ListItemId?: number;
    Kunde?: SpartaxKunde;
    Auftragskunden?: SpartaxAuftragskunde[];
    Status?: Auftragstatus;
    StatusSeit?: Date;
    /** Zeitpunkte der Statuswechsel (Beginn je Status), berechnet beim Wechsel in Abg zu den Tage-Feldern. */
    StatusHistorie?: SpartaxStatusHistorieEintrag[];
    /** Kalendertage je Status (ohne Abg), geschrieben beim Wechsel in Abg. */
    TageStatusNeu?: number;
    TageStatusWaD?: number;
    TageStatusIB?: number;
    TageStatusWaB?: number;
    TageStatusIA?: number;
    TageStatusRo?: number;
    TageStatusRb?: number;
    Partner?: SpartaxPartner;
    Berater?: SpartaxUser;
    Sachbearbeiter?: SpartaxUser;
    Info?: string;
    Finanzamt?: string;
    Rueckzahlung?: number;
    RechnungssummeNetto?: number;
    RechnungssummeBrutto?: number;
    Partnerprovision?: number;
    ProvisionssatzEffektiv?: number;
    RechnungssummeAusImport?: boolean;
    LetztesRechnungsdatum?: Date;
    Fehlerstatus?: AuftragFehlerstatus;
    Fehlerbeschreibung?: string;
    Dokumente?: SpartaxDocument[];
    AdditionalData?: string;
    BeeexpertFormId?: string;
    BeeexpertFormFilloutId?: string;
    BeeexpertCQFormId?: string;
    BeeexpertCQFormFilloutId?: string;
    IsSelfService?: boolean;
    FristUnterlagenBereitstellen?: Date;
    FristBelegurgenz?: Date;
    Created?: Date;
    Modified?: Date;
    Author?: SpartaxUser;
    Editor?: SpartaxUser;
    KommunikationAlt?: SpartaxKommunikationEintragAlt[];
    Kommunikation?: SpartaxKommunikation[];
    Rechnungen?: SpartaxRechnung[];
    SevDeskReference?: SpartaxSevDeskReference;
    /* TODO -> Remove after migrating data and delete fields in SharePoint (or remove if not needed) */
    SevDeskInvoiceIds?: string[];
    SevDeskInvoiceInfo?: string; // HTML mit Links zu SevDesk-Rechnungen (Legacy-Anzeige)
    SevDeskTotalInvoiceCreated?: boolean;
    SevDeskCreditNoteId?: string;
    /* END TODO */
    FreeFinanceInvoiceIds?: string[];
    FreeFinanceTotalInvoiceCreated?: boolean;
    FreeFinanceCreditNoteId?: string;
    FollowUpData?: SpartaxFolgeAuftragDto;
    //for future configuribility / override feature:
    Sockelbetrag?: number;
    HonorarBasisMin?: number;
    HonorarBasisMax?: number;
    HonorarSatzMin?: number;
    HonorarSatzMax?: number;
    HonorarWiederaufnahme?: number;
    HonorarPflichtveranlagung?: number;
    HonorarEinnahmenAusgabenRechnungGewerbe?: number;
    HonorarEinnahmenAusgabenRechnungVermietung?: number;
};

export class SpartaxAuftragUtil {
    /** SharePoint-Feldname je Status für die Kalendertage. Abgeschlossen (Abg) ist bewusst nicht enthalten. */
    public static readonly TageStatusFieldNames = new Map<Auftragstatus, string>([
        [Auftragstatus.Neu, 'TageStatusNeu'],
        [Auftragstatus.waD, 'TageStatusWaD'],
        [Auftragstatus.iB, 'TageStatusIB'],
        [Auftragstatus.waB, 'TageStatusWaB'],
        [Auftragstatus.iA, 'TageStatusIA'],
        [Auftragstatus.Ro, 'TageStatusRo'],
        [Auftragstatus.Rb, 'TageStatusRb'],
    ]);

    /** Parst die JSON-StatusHistorie aus einem SharePoint-Note-Feld (tolerant, verwirft ungültige Einträge). */
    public static parseStatusHistorie(raw: string | null | undefined): SpartaxStatusHistorieEintrag[] {
        if (!raw) {
            return [];
        }
        try {
            const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
            if (!Array.isArray(parsed)) {
                return [];
            }
            const result: SpartaxStatusHistorieEintrag[] = [];
            for (const entry of parsed) {
                if (!entry || !entry.Status || !entry.Seit) {
                    continue;
                }
                const seit = moment(entry.Seit).toDate();
                if (isNaN(seit.getTime())) {
                    continue;
                }
                result.push({ Status: entry.Status as Auftragstatus, Seit: seit });
            }
            return result;
        } catch {
            return [];
        }
    }

    /** Serialisiert die StatusHistorie für das SharePoint-Note-Feld. */
    public static serializeStatusHistorie(entries: SpartaxStatusHistorieEintrag[]): string {
        return JSON.stringify((entries || []).map(x => ({
            Status: x.Status,
            Seit: moment(x.Seit).toISOString()
        })));
    }

    /** Hängt einen Statuswechsel an (idempotent: kein Eintrag, wenn der letzte Eintrag denselben Status hat). */
    public static appendStatusHistorie(entries: SpartaxStatusHistorieEintrag[],
        status: Auftragstatus, when: Date): SpartaxStatusHistorieEintrag[] {
        const result = [...(entries || [])];
        if (result.length > 0 && result[result.length - 1].Status === status) {
            return result;
        }
        result.push({ Status: status, Seit: when });
        return result;
    }

    /**
     * Berechnet die Kalendertage je Status aus der StatusHistorie (startOf('day')-Differenz).
     * Mehrfach durchlaufene Status werden summiert; Abg wird nicht gezählt.
     * @param entries StatusHistorie (Beginn je Status, chronologisch)
     * @param skipFirstPeriod true: die erste Periode nicht zählen (unbekannter Start, z. B. Backfill-Lücke)
     */
    public static calculateTageProStatus(entries: SpartaxStatusHistorieEintrag[],
        skipFirstPeriod = false): Map<Auftragstatus, number> {
        const result = new Map<Auftragstatus, number>();
        for (const status of Object.values(Auftragstatus)) {
            result.set(status as Auftragstatus, 0);
        }
        if (!entries || entries.length < 2) {
            return result;
        }
        for (let i = 0; i < entries.length - 1; i++) {
            if (skipFirstPeriod && i === 0) {
                continue;
            }
            const status = entries[i].Status;
            if (status === Auftragstatus.Abg || status === Auftragstatus.Sto) {
                continue;
            }
            const start = moment(entries[i].Seit).startOf('day');
            const end = moment(entries[i + 1].Seit).startOf('day');
            if (!start.isValid() || !end.isValid()) {
                continue;
            }
            const days = end.diff(start, 'days');
            if (days <= 0) {
                continue;
            }
            result.set(status, (result.get(status) ?? 0) + days);
        }
        return result;
    }

    /**
     * Mappt die berechneten Tage auf die SharePoint-Feldnamen (alle linearen Status außer Abg).
     * @param unknownStatuses Status, deren Wert nicht exakt bestimmt werden konnte: werden als
     * `null` (= Feld leeren) statt `0` geschrieben.
     */
    public static getTageStatusFieldValues(tage: Map<Auftragstatus, number>,
        unknownStatuses?: Set<Auftragstatus> | null): Record<string, number | null> {
        const result: Record<string, number | null> = {};
        for (const [status, fieldName] of this.TageStatusFieldNames) {
            result[fieldName] = unknownStatuses?.has(status) ? null : (tage.get(status) ?? 0);
        }
        return result;
    }

    /**
     * Ermittelt die Status, deren Tage-Wert sich aus der Historie nicht exakt bestimmen lässt.
     * Nur bei unvollständigem Versionsfenster (`windowComplete = false`) relevant:
     * - Status ohne Eintrag in der Historie können im unbekannten Zeitraum durchlaufen worden sein.
     * - Der Status des ersten Eintrags hat einen unbekannten Start (Dauer nur teilweise bekannt).
     */
    public static getUnbekannteTageStatuses(entries: SpartaxStatusHistorieEintrag[],
        windowComplete: boolean): Set<Auftragstatus> {
        const result = new Set<Auftragstatus>();
        if (windowComplete) {
            return result;
        }
        const beobachtet = new Set<Auftragstatus>((entries || []).map(x => x.Status));
        for (const status of this.TageStatusFieldNames.keys()) {
            if (!beobachtet.has(status)) {
                result.add(status);
            }
        }
        if (entries && entries.length > 0 && this.TageStatusFieldNames.has(entries[0].Status)) {
            result.add(entries[0].Status);
        }
        return result;
    }

    /**
     * Erzeugt die Statushistorie-bezogenen Feldwerte für einen Statuswechsel
     * (StatusHistorie, und bei Abg zusätzlich die 7 Tage-Felder).
     */
    public static getStatusHistorieFieldValues(existingHistorie: SpartaxStatusHistorieEintrag[],
        newStatus: Auftragstatus, transitionTime: Date): Record<string, any> {
        const historie = this.appendStatusHistorie(existingHistorie, newStatus, transitionTime);
        const fieldValues: Record<string, any> = {
            StatusHistorie: this.serializeStatusHistorie(historie)
        };
        if (newStatus === Auftragstatus.Abg) {
            Object.assign(fieldValues, this.getTageStatusFieldValues(this.calculateTageProStatus(historie)));
        }
        return fieldValues;
    }

    /**
     * Rekonstruiert die StatusHistorie aus der List-Item-Versionierung (nur die letzten ~50 Versionen verfügbar).
     * @param versions Versionsarray aus PnPjs (`Status`, `Created`, `VersionLabel`)
     * @param currentStatus aktueller Status des Auftrags
     * @param statusSeit StatusSeit des Auftrags (Start des aktuellen Status, falls vorhanden)
     * @param itemCreated Created des Auftrags
     * @returns historie + windowComplete (true, wenn die älteste Version die Version 1.0 ist)
     */
    public static buildStatusHistorieFromVersions(versions: any[], currentStatus: Auftragstatus | null,
        statusSeit: Date | null, itemCreated: Date | null): { historie: SpartaxStatusHistorieEintrag[]; windowComplete: boolean } {
        const sorted = [...(versions || [])]
            .filter(v => v && v.Created)
            .sort((a, b) => moment(a.Created).valueOf() - moment(b.Created).valueOf());

        const historie: SpartaxStatusHistorieEintrag[] = [];
        let prevStatus: string | null = null;
        for (const v of sorted) {
            const status = v.Status;
            if (!status) {
                continue;
            }
            if (status !== prevStatus) {
                historie.push({ Status: status as Auftragstatus, Seit: moment(v.Created).toDate() });
                prevStatus = status;
            }
        }

        const windowComplete = sorted.length > 0 && String(sorted[0].VersionLabel) === '1.0';

        // StatusSeit kann veraltet sein (z. B. wenn ein späterer Statuswechsel es nicht aktualisiert hat)
        // oder durch Zeitzonen-Interpretation verschoben sein. Daher die Versionierung als Quelle der
        // Wahrheit bevorzugen und StatusSeit nur verwenden, wenn es den Eintrag zeitlich nach vorne
        // verschiebt. So bleibt StatusSeit der "späteste bekannte" Start des aktuellen Status und die
        // Historie chronologisch.
        const statusSeitDate = statusSeit && moment(statusSeit).isValid() ? moment(statusSeit).toDate() : null;
        if (currentStatus) {
            const lastIndex = historie.length - 1;
            if (lastIndex >= 0 && historie[lastIndex].Status === currentStatus) {
                if (statusSeitDate && statusSeitDate.getTime() > historie[lastIndex].Seit.getTime()) {
                    historie[lastIndex].Seit = statusSeitDate;
                }
            } else {
                let when = statusSeitDate ?? moment().toDate();
                if (lastIndex >= 0 && when.getTime() < historie[lastIndex].Seit.getTime()) {
                    when = historie[lastIndex].Seit;
                }
                historie.push({ Status: currentStatus, Seit: when });
            }
        }

        // Den Neu-Start nur nach vorne verschieben (frühester bekannter Zeitpunkt), damit die
        // Reihenfolge nicht durch ein abweichendes Created-Feld invertiert wird.
        if (windowComplete && itemCreated && historie.length > 0 && historie[0].Status === Auftragstatus.Neu) {
            const created = moment(itemCreated).toDate();
            if (created.getTime() < historie[0].Seit.getTime()) {
                historie[0].Seit = created;
            }
        }

        return { historie, windowComplete };
    }

    public static getStatusFormatted(status: Auftragstatus | null) {
        if (status) {
            return `${AuftragStatusName.get(status)} (${Auftragstatus[status]})`;
        } else {
            return '-';
        }
    }

    public static getStatusPercentComplete(status: Auftragstatus | null) {
        if (!status) {
            return 0;
        }
        const currentKey = AuftragStatusOrder.indexOf(status);
        if (currentKey < 0) {
            return 0;
        }
        return (currentKey / (AuftragStatusOrder.length - 1));
    }

    public static getNextStatus(currentStatus: Auftragstatus | null): Auftragstatus | null {
        const currentKey = currentStatus ? AuftragStatusOrder.indexOf(currentStatus) : -1;
        if (currentKey < 0 || currentKey >= AuftragStatusOrder.length - 1) {
            return null;
        }
        return AuftragStatusOrder[currentKey + 1];
    }

    public static getPreviousStatus(currentStatus: Auftragstatus | null): Auftragstatus | null {
        const currentKey = currentStatus ? AuftragStatusOrder.indexOf(currentStatus) : -1;
        if (currentKey <= 0) {
            return null;
        }
        return AuftragStatusOrder[currentKey - 1];
    }

    public static isAuftragInMinimalStatus(currentStatus: Auftragstatus | null, minimumStatus: Auftragstatus) {
        if (!currentStatus || currentStatus === Auftragstatus.Sto) {
            return false;
        }
        const currentKey = AuftragStatusOrder.indexOf(currentStatus);
        const minimumKey = AuftragStatusOrder.indexOf(minimumStatus);
        if (currentKey < 0 || minimumKey < 0) {
            return false;
        }
        return currentKey >= minimumKey;
    }

    public static isStorniert(status: Auftragstatus | null | undefined): boolean {
        return status === Auftragstatus.Sto;
    }

    /** Provisionsrelevant sind alle linearen Status ab 'In Bearbeitung' (inkl. Abg). */
    public static isProvisionRelevant(status: Auftragstatus | null | undefined): boolean {
        if (!status || status === Auftragstatus.Sto) {
            return false;
        }
        const statusValue = AuftragStatusReihenholge.get(status);
        const minValue = AuftragStatusReihenholge.get(Auftragstatus.iB);
        return statusValue !== undefined && minValue !== undefined && statusValue >= minValue;
    }

    /**
     * Liefert den Status vor dem letzten Storno-Eintrag aus der StatusHistorie.
     * Mehrfache Storno-Einträge werden übersprungen. Ohne Treffer: null.
     */
    public static getStatusBeforeStorno(historie: SpartaxStatusHistorieEintrag[] | null | undefined): Auftragstatus | null {
        if (!historie || historie.length === 0) {
            return null;
        }
        const lastStornoIndex = historie.map(x => x.Status).lastIndexOf(Auftragstatus.Sto);
        if (lastStornoIndex < 0) {
            return null;
        }
        for (let i = lastStornoIndex - 1; i >= 0; i--) {
            if (historie[i].Status !== Auftragstatus.Sto) {
                return historie[i].Status;
            }
        }
        return null;
    }

    public static getAuftragsdokumenteServerRelativeUrl(auftrag: SpartaxAuftrag, spWebServerRelativeUrl: string): string {
        if (auftrag.ListItemId) {
            return `${spWebServerRelativeUrl}/Auftragsdokumente/Auftragsdokumente ${auftrag.ListItemId}`;
        } else {
            return null;
        }
    }

    public static getRelevantHonorarConfig(auftrag: SpartaxAuftrag, honorarConfigs: SpartaxKonfigHonorare[]) {
        if (honorarConfigs && honorarConfigs.length > 0) {
            const honorarConfigsSorted = from(honorarConfigs).orderByDescending(x => moment(x.GueltigAb).unix()).toArray();
            const honorarConfig = from(honorarConfigsSorted).firstOrDefault(x => moment(x.GueltigAb).toDate() <= moment(auftrag.Created).toDate());
            return honorarConfig;
        } else {
            return { BasisMin: 0, BasisMax: 0, SatzMin: 0, SatzMax: 0, Wiederaufnahme: 0, Pflichtveranlagung: 0, EAGewerbe: 0, EAVermietung: 0 };
        }
    }

    public static getSockelbetrag(auftrag: SpartaxAuftrag) {
        return auftrag.Sockelbetrag || 0;
    }

    public static getHonorarBasisMin(auftrag: SpartaxAuftrag, honorarConfigs: SpartaxKonfigHonorare[]) {
        const relevantConfig = this.getRelevantHonorarConfig(auftrag, honorarConfigs);
        return auftrag.HonorarBasisMin || relevantConfig.BasisMin || 0;
    }

    public static getHonorarBasisMax(auftrag: SpartaxAuftrag, honorarConfigs: SpartaxKonfigHonorare[]) {
        const relevantConfig = this.getRelevantHonorarConfig(auftrag, honorarConfigs);
        return auftrag.HonorarBasisMax || relevantConfig.BasisMax || 0;
    }

    public static getHonorarBasisSatzMin(auftrag: SpartaxAuftrag, honorarConfigs: SpartaxKonfigHonorare[]) {
        const relevantConfig = this.getRelevantHonorarConfig(auftrag, honorarConfigs);
        return auftrag.HonorarSatzMin || relevantConfig.SatzMin || 0;
    }

    public static getHonorarBasisSatzMax(auftrag: SpartaxAuftrag, honorarConfigs: SpartaxKonfigHonorare[]) {
        const relevantConfig = this.getRelevantHonorarConfig(auftrag, honorarConfigs);
        return auftrag.HonorarSatzMax || relevantConfig.SatzMax || 0;
    }

    public static getHonorarBasisWiederaufnahme(auftrag: SpartaxAuftrag, honorarConfigs: SpartaxKonfigHonorare[]) {
        const relevantConfig = this.getRelevantHonorarConfig(auftrag, honorarConfigs);
        return auftrag.HonorarWiederaufnahme || relevantConfig.Wiederaufnahme || 0;
    }

    public static getHonorarBasisPflichtveranlagung(auftrag: SpartaxAuftrag, honorarConfigs: SpartaxKonfigHonorare[]) {
        const relevantConfig = this.getRelevantHonorarConfig(auftrag, honorarConfigs);
        return auftrag.HonorarPflichtveranlagung || relevantConfig.Pflichtveranlagung || 0;
    }

    public static getHonorarBasisEinnahmenAusgabenRechnungGewerbe(auftrag: SpartaxAuftrag, honorarConfigs: SpartaxKonfigHonorare[]) {
        const relevantConfig = this.getRelevantHonorarConfig(auftrag, honorarConfigs);
        return auftrag.HonorarEinnahmenAusgabenRechnungGewerbe || relevantConfig.EAGewerbe || 0;
    }

    public static getHonorarBasisEinnahmenAusgabenRechnungVermietung(auftrag: SpartaxAuftrag, honorarConfigs: SpartaxKonfigHonorare[]) {
        const relevantConfig = this.getRelevantHonorarConfig(auftrag, honorarConfigs);
        return auftrag.HonorarEinnahmenAusgabenRechnungVermietung || relevantConfig.EAVermietung || 0;
    }

    public static getTotalRueckzahlungen(auftrag: SpartaxAuftrag, ignoreNegativeValues: boolean) {
        return from(auftrag.Auftragskunden).sum(x => SpartaxAuftragskundeUtil.getRueckzahlungen(x, ignoreNegativeValues));
    }

    public static calculateRueckzahlungSockelbetrag(auftrag: SpartaxAuftrag) {
        const sockelbetrag = this.getSockelbetrag(auftrag);
        const rueckzahlung = this.getTotalRueckzahlungen(auftrag, true);
        return sockelbetrag < rueckzahlung ? sockelbetrag : rueckzahlung;
    }

    public static calculateHonorarSockelbetrag(auftrag: SpartaxAuftrag, honorarConfigs: SpartaxKonfigHonorare[]) {
        return this.calculateRueckzahlungSockelbetrag(auftrag) * this.getHonorarBasisSatzMax(auftrag, honorarConfigs) / 1.2;
    }

    public static calculateRueckzahlungAbzglSockelbetrag(auftrag: SpartaxAuftrag) {
        return this.getTotalRueckzahlungen(auftrag, true) - this.calculateRueckzahlungSockelbetrag(auftrag);
    }

    public static calculateHonorarSatzRest(auftrag: SpartaxAuftrag, honorarConfigs: SpartaxKonfigHonorare[]) {
        if (this.calculateRueckzahlungAbzglSockelbetrag(auftrag) <= this.getHonorarBasisMin(auftrag, honorarConfigs)) {
            return this.getHonorarBasisSatzMax(auftrag, honorarConfigs);
        } else if (this.calculateRueckzahlungAbzglSockelbetrag(auftrag) >= this.getHonorarBasisMax(auftrag, honorarConfigs)) {
            return this.getHonorarBasisSatzMin(auftrag, honorarConfigs);
        } else {
            return this.getHonorarBasisSatzMax(auftrag, honorarConfigs) -
                (this.calculateRueckzahlungAbzglSockelbetrag(auftrag) - this.getHonorarBasisMin(auftrag, honorarConfigs)) *
                (this.getHonorarBasisSatzMax(auftrag, honorarConfigs) - this.getHonorarBasisSatzMin(auftrag, honorarConfigs)) /
                (this.getHonorarBasisMax(auftrag, honorarConfigs) - this.getHonorarBasisMin(auftrag, honorarConfigs));
        }
    }

    public static calculateHonorarRest(auftrag: SpartaxAuftrag, honorarConfigs: SpartaxKonfigHonorare[]) {
        return this.calculateRueckzahlungAbzglSockelbetrag(auftrag) * this.calculateHonorarSatzRest(auftrag, honorarConfigs) / 1.2;
    }

    public static calculateHonorarRueckzahlungGesamt(auftrag: SpartaxAuftrag, honorarConfigs: SpartaxKonfigHonorare[]) {
        return this.calculateHonorarSockelbetrag(auftrag, honorarConfigs) + this.calculateHonorarRest(auftrag, honorarConfigs);
    }

    public static calculateMischSatz(auftrag: SpartaxAuftrag, honorarConfigs: SpartaxKonfigHonorare[]) {
        return this.calculateHonorarRueckzahlungGesamt(auftrag, honorarConfigs) * 1.2 / this.getTotalRueckzahlungen(auftrag, true);
    }

    public static calculateAuftragHonorarBasis(auftrag: SpartaxAuftrag, fragen: SpartaxFrage[], honorarConfigs: SpartaxKonfigHonorare[]) {
        return from(auftrag.Auftragskunden)
            .sum(x => SpartaxAuftragskundeUtil.calculateHonorarBasis(x, fragen, auftrag, honorarConfigs));
    }

    public static calculateAuftragHonorar(auftrag: SpartaxAuftrag, fragen: SpartaxFrage[], honorarConfigs: SpartaxKonfigHonorare[]) {
        return from(auftrag.Auftragskunden)
            .sum(x => SpartaxAuftragskundeUtil.calculateHonorarGesamt(x, fragen, auftrag, honorarConfigs));
    }

    public static calculateAuftragRechnungsbetragVerrechnet(auftrag: SpartaxAuftrag) {
        if (auftrag.Rechnungen) {
            return from(auftrag.Rechnungen).where(isRechnungAktiv).sum(x => +x.BetragNetto);
        } else {
            return 0;
        }
    }

    public static calculateAuftragHonorarNichtVerrechnet(auftrag: SpartaxAuftrag, fragen: SpartaxFrage[], honorarConfigs: SpartaxKonfigHonorare[]) {
        return from(auftrag.Auftragskunden)
            .sum(x => SpartaxAuftragskundeUtil.calculateHonorarNichtVerrechnet(x, fragen, auftrag, honorarConfigs));
    }

    public static getAuftragskundenSorted(auftrag: SpartaxAuftrag) {
        const visibleHauptkunde = this.getVisibleHauptkunde(auftrag);
        return from(auftrag.Auftragskunden).orderBy(x => {
            if (x.Kunde.ListItemId === visibleHauptkunde.Kunde.ListItemId) {
                return 0;
            } else if (x.AuftragsKundeArt === SpartaxAuftragskundeArt.kind) {
                return 2;
            } else {
                return 1;
            }
        }).toArray();
    }

    public static getAuftragskundenWhereSvNummerMissing(auftrag: SpartaxAuftrag) {
        const auftragskundenSorted = this.getAuftragskundenSorted(auftrag);
        return from(auftragskundenSorted).where(x => SpartaxAuftragskundeUtil.isSvNummerMissing(x)).toArray();
    }

    public static getAuftragskundenWhereAusweisMissing(auftrag: SpartaxAuftrag) {
        const auftragskundenSorted = this.getAuftragskundenSorted(auftrag);
        return from(auftragskundenSorted).where(x => SpartaxAuftragskundeUtil.isAusweisMissing(x)).toArray();
    }

    public static getAuftragskundenWhereVollmachtMissing(auftrag: SpartaxAuftrag) {
        const auftragskundenSorted = this.getAuftragskundenSorted(auftrag);
        return from(auftragskundenSorted).where(x => SpartaxAuftragskundeUtil.isVollmachtMissing(x)).toArray();
    }

    public static getAuftragskundenWhereVollmachtPartnerMissing(auftrag: SpartaxAuftrag) {
        // Bestandsschutz: Aufträge die vor Einführung der Vollmacht-Pflicht erstellt wurden, brauchen keine Vollmacht.
        if (!VOLLMACHT_DEBUG_AUFTRAG_LISTITEMID.includes(auftrag.ListItemId) &&
            moment(auftrag.Created).isBefore(VOLLMACHT_PFLICHT_AB, 'day')) {
            return [];
        }
        const auftragskundenSorted = this.getAuftragskundenSorted(auftrag);
        return from(auftragskundenSorted).where(x => SpartaxAuftragskundeUtil.isVollmachtPartnerMissing(x)).toArray();
    }

    public static getJahreCount(auftrag: SpartaxAuftrag) {
        return from(auftrag.Auftragskunden).sum(x => SpartaxAuftragskundeUtil.getJahre(x).length);
    }

    public static getJahreFormatted(auftrag: SpartaxAuftrag) {
        let jahre = '';
        for (const kunde of this.getAuftragskundenSorted(auftrag)) {
            const jahreOfAuftragskunde = SpartaxAuftragskundeUtil.getJahreFormatted(kunde);
            if (jahreOfAuftragskunde && jahreOfAuftragskunde.length > 0) {
                if (jahre) {
                    jahre += '\n';
                }
                jahre += SpartaxKundeUtil.getVollstaendigerNameOfKunde(kunde.Kunde, true) + ': ' + jahreOfAuftragskunde;
            }
        }
        return jahre;
    }

    public static getRelevantJahreRangeOfAuftrag(auftrag: SpartaxAuftrag, fragen: SpartaxFrage[]) {
        let jahre = [];
        for (const kunde of this.getAuftragskundenSorted(auftrag)) {
            const relevanteFragenOfAuftragskunde = from(
                SpartaxAuftragskundeUtil.getAllRelevantFragenOfAuftragskunde(kunde, fragen))
                .select(x => x.ListItemId).toArray();
            const jahreWithEinkommen = SpartaxAuftragskundeUtil.getJahre(kunde);

            for (const jahr of kunde.Erklaerungsjahre) {
                const yearHasAntwort = from(jahr.FragenAntworten)
                    .where(x => !!x.Antwort && relevanteFragenOfAuftragskunde.includes(x.FrageListItemId)).any();
                if (yearHasAntwort && !jahre.includes(jahr.Jahr) && jahreWithEinkommen.includes(jahr.Jahr)) {
                    jahre.push(jahr.Jahr);
                }
            }
        }
        if (jahre.length > 0) {
            const minJahr = from(jahre).min();
            const maxJahr = from(jahre).max();
            if (minJahr === maxJahr) {
                return `${minJahr}`;
            } else {
                return `${minJahr} - ${maxJahr}`;
            }
        } else {
            return '-';
        }
    }

    public static getKindForAuftragskunde(auftrag: SpartaxAuftrag, kindFormModelId: string): SpartaxKind {
        return from(auftrag.Auftragskunden).selectMany(x => x.Kunde.Kinder).firstOrDefault(x => x.KundeFormModelId === kindFormModelId);
    }

    public static getAllReleventFragenOfAuftrag(auftrag: SpartaxAuftrag, fragen: SpartaxFrage[]) {
        const relevanteFragenIds: number[] = [];

        for (const auftragskunde of auftrag.Auftragskunden) {
            const relevantFragenOfKunde = SpartaxAuftragskundeUtil.getAllRelevantFragenOfAuftragskunde(auftragskunde, fragen);
            for (const relevantFrage of relevantFragenOfKunde) {
                if (!relevanteFragenIds.includes(relevantFrage.ListItemId)) {
                    relevanteFragenIds.push(relevantFrage.ListItemId);
                }
            }
        }

        const relevantFragen = from(fragen)
            .where(x => relevanteFragenIds.includes(x.ListItemId))
            .orderBy(x => x.Fragenkategorie.Reihenfolge)
            .thenBy(x => x.Reihenfolge)
            .toArray();

        return relevantFragen;
    }

    private static getBelegeNeededOfAuftrag(auftrag: SpartaxAuftrag, fragen: SpartaxFrage[], fragenkategorieId: number = null) {
        const relevanteFragen = this.getAllReleventFragenOfAuftrag(auftrag, fragen);
        let belegeNeeded = [];
        for (const auftragskunde of auftrag.Auftragskunden) {
            const relevantFragenIds =
                from(SpartaxAuftragskundeUtil.getAllRelevantFragenOfAuftragskunde(auftragskunde, relevanteFragen))
                    .select(x => x.ListItemId)
                    .toArray();

            const questionIdsOfCategory = from(relevanteFragen)
                .where(x => x.Fragenkategorie.ListItemId == fragenkategorieId).select(x => x.ListItemId);

            belegeNeeded = belegeNeeded.concat(from(auftragskunde.Erklaerungsjahre)
                .selectMany(x => x.FragenAntworten)
                .where(x => !!x.Antwort && relevantFragenIds.includes(x.FrageListItemId) &&
                    (!fragenkategorieId || questionIdsOfCategory.contains(x.FrageListItemId)))
                .toArray());
        }
        return belegeNeeded;
    }

    public static getBelegeNeededCountOfAuftrag(auftrag: SpartaxAuftrag, fragen: SpartaxFrage[], fragenkategorieId: number = null) {
        return this.getBelegeNeededOfAuftrag(auftrag, fragen, fragenkategorieId).length;
    }

    public static getBelegeReceivedCountOfAuftrag(auftrag: SpartaxAuftrag, fragen: SpartaxFrage[], fragenkategorieId: number = null) {
        const relevanteFragen = this.getAllReleventFragenOfAuftrag(auftrag, fragen);
        let numberOfBelegeReceived = 0;
        for (const auftragskunde of auftrag.Auftragskunden) {
            const relevantFragenIds =
                from(SpartaxAuftragskundeUtil.getAllRelevantFragenOfAuftragskunde(auftragskunde, relevanteFragen))
                    .select(x => x.ListItemId)
                    .toArray();

            const questionIdsOfCategory = from(relevanteFragen)
                .where(x => x.Fragenkategorie.ListItemId == fragenkategorieId).select(x => x.ListItemId);

            for (const jahr of auftragskunde.Erklaerungsjahre) {
                const antwortenOfJahr = from(jahr.FragenAntworten).where(x => !!x.Antwort)
                    .select(x => x.FrageListItemId).distinct().toArray();

                if (jahr.FragenBelegeErhalten && jahr.FragenBelegeErhalten.length > 0) {
                    numberOfBelegeReceived += from(jahr.FragenBelegeErhalten)
                        .where(x => !!x.BelegeErhaltenAm && relevantFragenIds.includes(x.FrageListItemId) &&
                            antwortenOfJahr.includes(x.FrageListItemId) &&
                            (!fragenkategorieId || questionIdsOfCategory.contains(x.FrageListItemId)))
                        .count();
                }
            }
        }
        return numberOfBelegeReceived;
    }

    public static getEinreichungenNeededCountOfAuftrag(auftrag: SpartaxAuftrag) {
        return from(auftrag.Auftragskunden)
            .where(x => x.TeilDesAuftrags)
            .selectMany(x => x.Erklaerungsjahre)
            .where(x => SpartaxErklaerungsjahrUtil.hasYearIncome(x))
            .count();
    }

    public static getEinreichungenReceivedCountOfAuftrag(auftrag: SpartaxAuftrag) {
        return from(auftrag.Auftragskunden)
            .where(x => x.TeilDesAuftrags)
            .selectMany(x => x.Erklaerungsjahre)
            .where(x => SpartaxErklaerungsjahrUtil.hasYearIncome(x) && x.Einreichdatum && moment(x.Einreichdatum).isValid())
            .count();
    }

    public static getBescheideNeededCountOfAuftrag(auftrag: SpartaxAuftrag) {
        return from(auftrag.Auftragskunden)
            .where(x => x.TeilDesAuftrags)
            .selectMany(x => x.Erklaerungsjahre)
            .where(x => SpartaxErklaerungsjahrUtil.hasYearIncome(x))
            .count();
    }

    public static getBescheideReceivedCountOfAuftrag(auftrag: SpartaxAuftrag) {
        return from(auftrag.Auftragskunden)
            .where(x => x.TeilDesAuftrags)
            .selectMany(x => x.Erklaerungsjahre)
            .where(x => SpartaxErklaerungsjahrUtil.hasYearIncome(x) && x.Bescheiddatum && moment(x.Bescheiddatum).isValid())
            .count();
    }

    public static getVisibleHauptkunde(auftrag: SpartaxAuftrag) {
        //Only adults that are not deceased and no children
        const customersFilteredAndSorted =
            from(auftrag.Auftragskunden)
                .where(x => x.TeilDesAuftrags && x.AuftragsKundeArt !== SpartaxAuftragskundeArt.kind &&
                    !!x.Erklaerungsjahre && x.Erklaerungsjahre.length > 0 &&
                    (x.AuftragsKundeArt.length > 1 ? !x.Kunde.Verstorben : true))
                .orderByDescending(x => SpartaxSchaetzungsJahreseinkunftArtOrder.indexOf(
                    from(x.Erklaerungsjahre || []).orderBy(x => x.Jahr).lastOrDefault()?.SchaetzungJahreseinkunft))
                .thenBy(x => x.Kunde.Geschlecht === 'Männlich' ? 0 : 1) //Sort by Geschlecht (inverted for descending)
                .toArray();

        if (customersFilteredAndSorted.length > 0) {
            return customersFilteredAndSorted[0];
        } else {
            return auftrag.Auftragskunden[0];
        }
    }

    public static getKontaktDesAuftrags(auftrag: SpartaxAuftrag) {
        return SpartaxAuftragskundeUtil.getKontaktDesAuftrags(auftrag.Auftragskunden);
    }

    public static getRelevanteProvisionskonfiguration(auftrag: SpartaxAuftrag, konfigProvisionen: SpartaxKonfigProvisionen[] | undefined): SpartaxKonfigProvisionen | null {
        if (!auftrag.Partner?.ListItemId || !auftrag.Created) {
            return null;
        }
        return this.getRelevanteProvisionskonfigurationFromValues(auftrag.Partner.ListItemId, auftrag.Created, konfigProvisionen);
    }

    public static getRelevanteProvisionskonfigurationFromValues(partnerId: number, created: Date, konfigProvisionen: SpartaxKonfigProvisionen[] | undefined): SpartaxKonfigProvisionen | null {
        if (!konfigProvisionen || konfigProvisionen.length === 0 || !partnerId || !created) {
            return null;
        }

        const relevanteProvisionen = from(konfigProvisionen)
            .where(x => x.PartnerId === partnerId && x.GueltigAb && moment(x.GueltigAb).isSameOrBefore(moment(created)))
            .orderByDescending(x => x.GueltigAb)
            .toArray();

        return relevanteProvisionen.length > 0 ? relevanteProvisionen[0] : null;
    }

    public static isAuftragProvisionUeberSchwelle(auftrag: SpartaxAuftrag, 
        provisionsRelevanteAuftragsIds: number[],
        konfigProvisionen: SpartaxKonfigProvisionen[] | undefined): boolean {
        if (!auftrag.ListItemId) {
            return false;
        }

        const konfig = this.getRelevanteProvisionskonfiguration(auftrag, konfigProvisionen);
        if (!konfig) {
            return false;
        }

        return this.isAuftragProvisionUeberSchwelleFromValues(auftrag.ListItemId, provisionsRelevanteAuftragsIds, konfig);
    }

    public static isAuftragProvisionUeberSchwelleFromValues(listItemId: number, 
        provisionsRelevanteAuftragsIds: number[],
        konfig: SpartaxKonfigProvisionen): boolean {
        if (!listItemId) {
            return false;
        }

        if (!konfig.Schwelle) {
            return false;
        }

        const position = provisionsRelevanteAuftragsIds.indexOf(listItemId);
        if (position === -1) {
            return false;
        }

        return position >= konfig.Schwelle;
    }

    public static getProvisionssatzEffektiv(auftrag: SpartaxAuftrag, konfigProvisionen: SpartaxKonfigProvisionen[] | undefined, provisionsRelevanteAuftragsIds: number[]): number | null {
        if (!auftrag.Partner?.ListItemId || !auftrag.Created) {
            return null;
        }
        return this.getProvisionssatzEffektivFromValues(auftrag.IsSelfService, auftrag.ListItemId, auftrag.Partner.ListItemId, auftrag.Created, konfigProvisionen, provisionsRelevanteAuftragsIds);
    }

    public static getProvisionssatzEffektivFromValues(isSelfService: boolean, listItemId: number, partnerId: number, created: Date,
        konfigProvisionen: SpartaxKonfigProvisionen[] | undefined, provisionsRelevanteAuftragsIds: number[]): number | null {
        const konfig = this.getRelevanteProvisionskonfigurationFromValues(partnerId, created, konfigProvisionen);
        if (!konfig) {
            return null;
        }

        let provisionssatz = (isSelfService && konfig.Provision2)
            ? konfig.Provision2
            : konfig.Provision1 ?? null;

        if (provisionssatz !== null && this.isAuftragProvisionUeberSchwelleFromValues(listItemId, provisionsRelevanteAuftragsIds, konfig)) {
            provisionssatz += konfig.ProvisionErhoehung ?? 0;
        }

        return provisionssatz;
    }

    public static stripAuftragOfSensitiveData(auftrag: SpartaxAuftrag) {
        SpartaxKundeUtil.stripKundeOfSensitiveData(auftrag.Kunde);
        if (auftrag.Auftragskunden) {
            for (const auftragskunde of auftrag.Auftragskunden) {
                SpartaxAuftragskundeUtil.stripAuftragskundeFromSensitiveData(auftragskunde);
            }
        }
        delete auftrag.Info;
        delete auftrag.Finanzamt;
        delete auftrag.Fehlerstatus;
        delete auftrag.Fehlerbeschreibung;
        delete auftrag.Dokumente;
        delete auftrag.AdditionalData;
        delete auftrag.Kommunikation;
        delete auftrag.KommunikationAlt;
        delete auftrag.Rechnungen;
        delete auftrag.Sockelbetrag;
        delete auftrag.HonorarBasisMin;
        delete auftrag.HonorarBasisMax;
        delete auftrag.HonorarSatzMin;
        delete auftrag.HonorarSatzMax;
        delete auftrag.HonorarWiederaufnahme;
        delete auftrag.HonorarPflichtveranlagung;
        delete auftrag.HonorarEinnahmenAusgabenRechnungGewerbe;
        delete auftrag.HonorarEinnahmenAusgabenRechnungVermietung;
    }
}

export type SpartaxKundeIdDto = {
    FormModelId: string;
    ListItemId: number;
};

export type SpartaxAuftragIdDto = {
    ListItemId: number;
    KundenIds: SpartaxKundeIdDto[];
};

export type SpartaxFolgeAuftragKundeDto = {
    FormModelId: string;
    AuftragsKundeArt: SpartaxAuftragskundeArt;
    Name: string;
};

export type SpartaxFolgeAuftragDto = {
    ListItemId: number;
    LastYearOfNewOrder: number;
    Kunden: SpartaxFolgeAuftragKundeDto[];
    ReferenceOrderListItemId: number;
    ReferenceBeeexpertFormId: string;
    ReferenceBeeexpertFormFilloutId: string;
};
